import { useState, useEffect, useCallback } from 'react';
import { getAdminPerformanceBonus, getAdminPerformanceBonusStats } from '../../api/adminApi';
import { fmtIST } from '../../utils/dateFormat';

const cyan = '#00e5ff';
const accent = '#cc66ff';
const green = '#66ff99';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
};

const fmtAmt = (n) => (Number(n) || 0).toFixed(2);

const AdminPerformanceBonus = () => {
  const [records, setRecords] = useState([]);
  const [availableRanks, setAvailableRanks] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [inputVal, setInputVal] = useState('');
  const [rank, setRank] = useState('all');
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  // Stats card (from/to date filter)
  const [statsFrom, setStatsFrom] = useState('');
  const [statsTo, setStatsTo] = useState('');
  const [stats, setStats] = useState({ totalBonusEarned: 0, totalUsers: 0 });
  const [statsLoading, setStatsLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    getAdminPerformanceBonus({ search, rank: rank !== 'all' ? rank : undefined, page, limit: 20 })
      .then((res) => {
        setRecords(res.data.records);
        setTotal(res.data.total);
        setPages(res.data.pages);
        setAvailableRanks(res.data.availableRanks || []);
      })
      .finally(() => setLoading(false));
  }, [search, rank, page]);

  useEffect(() => { load(); }, [load]);

  const loadStats = useCallback(() => {
    setStatsLoading(true);
    getAdminPerformanceBonusStats({ from: statsFrom || undefined, to: statsTo || undefined })
      .then((res) => setStats({ totalBonusEarned: res.data.totalBonusEarned, totalUsers: res.data.totalUsers }))
      .finally(() => setStatsLoading(false));
  }, [statsFrom, statsTo]);

  useEffect(() => { loadStats(); }, [loadStats]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(inputVal);
  };

  return (
    <div>
      <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}>Performance Bonus</h4>
      <div style={{ color: '#555', fontSize: 13, marginBottom: 24 }}>{total} rank achievements</div>

      {/* Filters */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '220px', gap: 12, marginBottom: 16 }}>
        <div>
          <label style={{ display: 'block', color: '#888', fontSize: 11, fontWeight: 600, marginBottom: 6 }}>Rank</label>
          <select
            value={rank}
            onChange={(e) => { setRank(e.target.value); setPage(1); }}
            style={{ width: '100%', background: '#1a1a1a', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 8, padding: '8px 12px', color: '#fff', fontSize: 13, outline: 'none', cursor: 'pointer' }}
          >
            <option value="all" style={{ background: '#1a1a1a', color: '#fff' }}>All Ranks</option>
            {availableRanks.map((r) => (
              <option key={r} value={r} style={{ background: '#1a1a1a', color: accent }}>{r}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
        <input
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          placeholder="Search by user code or name..."
          style={{ flex: 1, minWidth: isMobile ? '100%' : 'auto', background: 'rgba(0,229,255,0.04)', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 8, padding: '9px 14px', color: '#fff', fontSize: 13, outline: 'none' }}
        />
        <button type="submit" style={{ background: cyan, border: 'none', color: '#000', borderRadius: 8, padding: '9px 20px', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          Search
        </button>
        {search && (
          <button type="button" onClick={() => { setInputVal(''); setSearch(''); setPage(1); }}
            style={{ background: 'rgba(255,255,255,0.06)', border: 'none', color: '#888', borderRadius: 8, padding: '9px 14px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Clear
          </button>
        )}
      </form>

      {/* Table / Cards */}
      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {loading ? (
            <div style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>Loading...</div>
          ) : records.length === 0 ? (
            <div style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>No records found.</div>
          ) : (
            records.map((r) => (
              <div key={r._id} style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, padding: '16px' }}>
                <div style={{ color: '#fff', fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{r.userName}</div>
                <div style={{ color: cyan, fontSize: 11, fontFamily: 'monospace', marginBottom: 10 }}>{r.userCode}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 12 }}>
                  <div><div style={{ color: '#555', fontSize: 10 }}>Rank Achieved</div><div style={{ color: accent, fontWeight: 600 }}>{r.rank}</div></div>
                  <div><div style={{ color: '#555', fontSize: 10 }}>Received Bonus</div><div style={{ color: green, fontWeight: 600 }}>{fmtAmt(r.receivedBonus)} USDT</div></div>
                  <div style={{ gridColumn: '1 / -1' }}><div style={{ color: '#555', fontSize: 10 }}>Achieved On</div><div style={{ color: '#888' }}>{fmtIST(r.achievedAt)}</div></div>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(0,229,255,0.07)', borderBottom: '1px solid rgba(0,229,255,0.12)' }}>
                {['#', 'User Code', 'Name', 'Rank Achieved', 'Date & Time Achieved', 'Received Bonus'].map((h) => (
                  <th key={h} style={{ color: cyan, fontSize: 12, fontWeight: 600, padding: '12px 14px', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>Loading...</td></tr>
              ) : records.length === 0 ? (
                <tr><td colSpan={6} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>No records found.</td></tr>
              ) : records.map((r, i) => (
                <tr key={r._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ color: '#555', fontSize: 12, padding: '11px 14px' }}>{(page - 1) * 20 + i + 1}</td>
                  <td style={{ color: cyan, fontSize: 12, padding: '11px 14px', fontFamily: 'monospace' }}>{r.userCode}</td>
                  <td style={{ color: '#fff', fontSize: 13, fontWeight: 500, padding: '11px 14px', whiteSpace: 'nowrap' }}>{r.userName}</td>
                  <td style={{ padding: '11px 14px' }}>
                    <span style={{ background: 'rgba(204,102,255,0.12)', border: '1px solid rgba(204,102,255,0.3)', color: accent, borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
                      {r.rank}
                    </span>
                  </td>
                  <td style={{ color: '#888', fontSize: 12, padding: '11px 14px', whiteSpace: 'nowrap' }}>{fmtIST(r.achievedAt)}</td>
                  <td style={{ color: green, fontSize: 13, fontWeight: 600, padding: '11px 14px' }}>{fmtAmt(r.receivedBonus)} USDT</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
          {Array.from({ length: Math.min(pages, isMobile ? 5 : pages) }, (_, i) => i + 1).map((p) => (
            <button key={p} onClick={() => setPage(p)}
              style={{ background: p === page ? cyan : 'rgba(255,255,255,0.06)', border: 'none', color: p === page ? '#000' : '#888', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              {p}
            </button>
          ))}
          {isMobile && pages > 5 && <span style={{ color: '#555', fontSize: 12, padding: '6px 12px' }}>... {pages}</span>}
        </div>
      )}

      {/* Performance Bonus Stats */}
      <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, padding: 20, marginTop: 32 }}>
        <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, marginBottom: 16 }}>Performance Bonus Stats</div>

        {/* Date filter */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '160px 160px auto', gap: 12, marginBottom: 20, alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', color: '#888', fontSize: 11, fontWeight: 600, marginBottom: 6 }}>From Date</label>
            <input
              type="date"
              value={statsFrom}
              onChange={(e) => setStatsFrom(e.target.value)}
              style={{ width: '100%', background: '#1a1a1a', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 8, padding: '8px 12px', color: '#fff', fontSize: 13, outline: 'none' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', color: '#888', fontSize: 11, fontWeight: 600, marginBottom: 6 }}>To Date</label>
            <input
              type="date"
              value={statsTo}
              onChange={(e) => setStatsTo(e.target.value)}
              style={{ width: '100%', background: '#1a1a1a', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 8, padding: '8px 12px', color: '#fff', fontSize: 13, outline: 'none' }}
            />
          </div>
          {(statsFrom || statsTo) && (
            <button
              onClick={() => { setStatsFrom(''); setStatsTo(''); }}
              style={{ background: 'rgba(255,255,255,0.06)', border: 'none', color: '#888', borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer', fontWeight: 600, height: 'fit-content' }}
            >
              Clear
            </button>
          )}
        </div>

        {/* Stat tiles */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(2, 200px)', gap: 16 }}>
          <div style={{ background: 'rgba(102,255,153,0.06)', border: '1px solid rgba(102,255,153,0.25)', borderRadius: 10, padding: '16px 18px' }}>
            <div style={{ color: '#888', fontSize: 12, marginBottom: 6 }}>Total Bonus Earned</div>
            <div style={{ color: green, fontSize: 22, fontWeight: 700 }}>
              {statsLoading ? '...' : `${fmtAmt(stats.totalBonusEarned)} USDT`}
            </div>
          </div>
          <div style={{ background: 'rgba(0,229,255,0.06)', border: '1px solid rgba(0,229,255,0.25)', borderRadius: 10, padding: '16px 18px' }}>
            <div style={{ color: '#888', fontSize: 12, marginBottom: 6 }}>Total Users</div>
            <div style={{ color: cyan, fontSize: 22, fontWeight: 700 }}>
              {statsLoading ? '...' : stats.totalUsers}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPerformanceBonus;
